import { useAccount, useContractRead, useContractWrite, useContractReads, useWaitForTransaction, useSendTransaction } from 'wagmi';
import { formatEther, parseEther } from 'viem';
import { useState, useEffect } from 'react';
import { CONTRACT_ADDRESS, CONTRACT_ABI } from '../config/contract';

const DEV_ADDRESS = '0x57DeA3dd3B232eC25970DE32664786A72e25B072';

export function MintCard() {
  const { address, isConnected } = useAccount();
  const [globalCooldown, setGlobalCooldown] = useState(null);
  const [personalCooldown, setPersonalCooldown] = useState(null);
  const [donateEnabled, setDonateEnabled] = useState(true);
  const [donationAmount, setDonationAmount] = useState('0.05');
  const [isReady, setIsReady] = useState(false);

  const { data: lastMintTime } = useContractRead({
    address: CONTRACT_ADDRESS,
    abi: CONTRACT_ABI,
    functionName: 'lastMintTime',
    watch: true,
  });

  const { data: lastUserMint } = useContractRead({
    address: CONTRACT_ADDRESS,
    abi: CONTRACT_ABI,
    functionName: 'lastMintByAddress',
    args: [address],
    enabled: isConnected,
    watch: true,
  });

  const { data: stats } = useContractReads({
    contracts: [
      {
        address: CONTRACT_ADDRESS,
        abi: CONTRACT_ABI,
        functionName: 'currentReward',
      },
      {
        address: CONTRACT_ADDRESS,
        abi: CONTRACT_ABI,
        functionName: 'totalMints',
      },
      {
        address: CONTRACT_ADDRESS,
        abi: CONTRACT_ABI,
        functionName: 'INITIAL_REWARD',
      },
      {
        address: CONTRACT_ADDRESS,
        abi: CONTRACT_ABI,
        functionName: 'HALVING_INTERVAL',
      },
      {
        address: CONTRACT_ADDRESS,
        abi: CONTRACT_ABI,
        functionName: 'COOLDOWN_PERIOD',
      },
    ],
    watch: true,
  });

  const { write: mint, data: mintData } = useContractWrite({
    address: CONTRACT_ADDRESS,
    abi: CONTRACT_ABI,
    functionName: 'mint',
  });

  const { sendTransaction: sendDonation, data: donationData } = useSendTransaction();

  const { isLoading: isDonating } = useWaitForTransaction({
    hash: donationData?.hash,
  });

  const { isLoading: isMinting } = useWaitForTransaction({
    hash: mintData?.hash,
  });

  useEffect(() => {
    const updateTimers = () => {
      const now = Math.floor(Date.now() / 1000);
      
      // Global cooldown (2 minutes)
      if (lastMintTime) {
        const nextGlobalMint = Number(lastMintTime) + 120;
        const globalRemaining = Math.max(0, nextGlobalMint - now);
        setGlobalCooldown(globalRemaining);
      } else {
        setGlobalCooldown(0);
      }

      // Personal cooldown (20 minutes)
      if (lastUserMint) {
        const nextPersonalMint = Number(lastUserMint) + 1200;
        const personalRemaining = Math.max(0, nextPersonalMint - now);
        setPersonalCooldown(personalRemaining);
      } else {
        setPersonalCooldown(0);
      }

      // Update ready state
      setIsReady(globalCooldown === 0 && personalCooldown === 0);
    };

    const timer = setInterval(updateTimers, 1000);
    updateTimers(); // Initial update

    return () => clearInterval(timer);
  }, [lastMintTime, lastUserMint, globalCooldown, personalCooldown]);

  const formatWholeNumber = (value) => {
    if (!value) return '0';
    const number = Number(formatEther(value));
    return Math.floor(number).toLocaleString();
  };

  const formatTime = (seconds) => {
    if (!seconds) return 'Ready!';
    return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  };

  const handleDonationChange = (e) => {
    const value = e.target.value;
    if (value === '' || (/^\d*\.?\d*$/.test(value) && parseFloat(value) >= 0)) {
      setDonationAmount(value);
    }
  };

  const handleMint = async () => {
    try {
      // First send donation if enabled
      if (donateEnabled && donationAmount > 0) {
        await sendDonation({
          to: DEV_ADDRESS,
          value: parseEther(donationAmount),
        });
      }
      
      // Then mint BMC
      await mint();
    } catch (error) {
      console.error('Error during donation or mint:', error);
    }
  };

  const handleDonateOnly = async () => {
    try {
      if (donateEnabled && donationAmount > 0) {
        await sendDonation({
          to: DEV_ADDRESS,
          value: parseEther(donationAmount),
        });
      }
    } catch (error) {
      console.error('Error during donation:', error);
    }
  };

  const isProcessing = isDonating || isMinting;

  const getButtonText = () => {
    if (!isConnected) return 'Connect Wallet';
    if (isDonating) return 'Sending Donation...';
    if (isMinting) return 'Minting...';

    const cooldownTime = Math.max(globalCooldown, personalCooldown);
    if (cooldownTime > 0) {
      return donateEnabled 
        ? `Wait ${formatTime(cooldownTime)} - Click to Donate ${donationAmount} $AVAX`
        : `Wait ${formatTime(cooldownTime)}`;
    }

    return donateEnabled 
      ? `Mint BMC + ${donationAmount} $AVAX`
      : 'Mint BMC';
  };

  const handleClick = async () => {
    try {
      // If in cooldown and donation enabled, only do donation
      if ((globalCooldown > 0 || personalCooldown > 0) && donateEnabled && donationAmount > 0) {
        await sendDonation({
          to: DEV_ADDRESS,
          value: parseEther(donationAmount),
        });
        return;
      }

      // If ready to mint
      if (globalCooldown === 0 && personalCooldown === 0) {
        if (donateEnabled && donationAmount > 0) {
          await sendDonation({
            to: DEV_ADDRESS,
            value: parseEther(donationAmount),
          });
        }
        await mint();
      }
    } catch (error) {
      console.error('Error during transaction:', error);
    }
  };

  return (
    <div className="card mint-card">
      <h2>Mint BMC</h2>
      
      <div className="mint-info-grid">
        <div className="mint-stat">
          <span>Current Reward</span>
          <span>{formatWholeNumber(stats?.[0]?.result)} BMC</span>
        </div>
        <div className="mint-stat">
          <span>Global Cooldown</span>
          <span>{formatTime(globalCooldown)}</span>
        </div>
        {isConnected && (
          <>
            <div className="mint-stat">
              <span>Your Address</span>
              <span>{address.slice(0, 6)}...{address.slice(-4)}</span>
            </div>
            <div className="mint-stat">
              <span>Personal Cooldown</span>
              <span>{formatTime(personalCooldown)}</span>
            </div>
          </>
        )}
      </div>

      <div className="donation-section">
        <label className="donation-checkbox">
          <input
            type="checkbox"
            checked={donateEnabled}
            onChange={(e) => setDonateEnabled(e.target.checked)}
          />
          <span>Support the Developer</span>
        </label>
        <div className="donation-amount" style={{ opacity: donateEnabled ? 1 : 0.5 }}>
          <input
            type="text"
            value={donationAmount}
            onChange={handleDonationChange}
            placeholder="0.05"
            disabled={!donateEnabled}
          />
          <span className="currency">$AVAX</span>
        </div>
      </div>

      <button
        className="mint-button"
        disabled={!isConnected || isProcessing}
        onClick={handleClick}
      >
        {getButtonText()}
      </button>
    </div>
  );
} 